import dayjs from "dayjs";
import {
  DBS_TYPE_OPTIONS,
  WORKFORCE_OPTIONS,
  dbsTypeRequiresWorkforce,
} from "../../../helpers/dbsCertHelper";

const EXPERIENCE_RANK = {
  "New to Sector": 1,
  "Developing Experience": 2,
  Competent: 3,
  "Highly Experienced": 4,
};

export function normalizeShiftDate(value) {
  if (!value) return null;
  if (typeof value === "string") {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
  }
  if (typeof value === "object" && value.date) {
    return normalizeShiftDate(value.date);
  }
  return null;
}

export function getPatternDates(pattern) {
  if (!Array.isArray(pattern?.dates)) return [];
  return pattern.dates
    .map((entry) => normalizeShiftDate(entry))
    .filter(Boolean);
}

export function getPatternRoleSlug(pattern) {
  return String(pattern?.role?.slug || pattern?.roleSlug || "").trim();
}

export function getPatternRoleTitle(pattern) {
  return String(pattern?.role?.title || pattern?.role?.name || "Role").trim();
}

export function getPatternExperienceLevels(pattern) {
  if (Array.isArray(pattern?.experienceLevel)) {
    return pattern.experienceLevel.map(String).filter(Boolean);
  }
  if (pattern?.experienceLevel) return [String(pattern.experienceLevel)];
  return [];
}

export function getPatternCertSlugs(pattern) {
  if (Array.isArray(pattern?.certSlugs)) {
    return pattern.certSlugs.map(String).filter(Boolean);
  }
  if (Array.isArray(pattern?.certs)) {
    return pattern.certs
      .map((c) => c?.slug || c?.certSlug)
      .filter(Boolean)
      .map(String);
  }
  return [];
}

export function getPatternDbsType(pattern) {
  return String(pattern?.dbsType || "").trim();
}

export function getPatternWorkForce(pattern) {
  return String(pattern?.workForce || pattern?.dbsWorkForce || "").trim();
}

export function getExistingWorkersForDate(pattern, dateIso) {
  if (!Array.isArray(pattern?.dates)) return [];
  const entry = pattern.dates.find(
    (d) => normalizeShiftDate(d) === normalizeShiftDate(dateIso)
  );
  if (!entry || typeof entry !== "object") return [];
  return Array.isArray(entry.workers) ? entry.workers : [];
}

export function getAssignedWorkerKey(worker) {
  return String(worker?.user?.slug || worker?.workerSlug || worker?.workerId || "")
    .trim()
    .toLowerCase();
}

/**
 * Draft assignment shape:
 * { shiftIndex, workerSlug, dates: string[] }
 */
export function getDraftAssignedDates(draftAssignments, shiftIndex, workerSlug) {
  const entry = (draftAssignments || []).find(
    (a) =>
      a.shiftIndex === shiftIndex &&
      String(a.workerSlug) === String(workerSlug)
  );
  return entry?.dates ? [...entry.dates] : [];
}

export function getFilledCountForDate(
  pattern,
  shiftIndex,
  dateIso,
  draftAssignments
) {
  const existing = getExistingWorkersForDate(pattern, dateIso).length;
  const draftCount = (draftAssignments || []).reduce((sum, a) => {
    if (a.shiftIndex !== shiftIndex) return sum;
    return sum + (a.dates || []).filter((d) => d === dateIso).length;
  }, 0);
  return existing + draftCount;
}

export function getRemainingCapacityForDate(
  pattern,
  shiftIndex,
  dateIso,
  draftAssignments
) {
  const quantity = Number(pattern?.quantity) || 0;
  const filled = getFilledCountForDate(
    pattern,
    shiftIndex,
    dateIso,
    draftAssignments
  );
  return Math.max(0, quantity - filled);
}

export function getShiftFillSummary(pattern, shiftIndex, draftAssignments) {
  const dates = getPatternDates(pattern);
  const quantity = Number(pattern?.quantity) || 0;
  const totalSlots = quantity * dates.length;
  let filledSlots = 0;
  dates.forEach((dateIso) => {
    filledSlots += getFilledCountForDate(
      pattern,
      shiftIndex,
      dateIso,
      draftAssignments
    );
  });
  return {
    datesCount: dates.length,
    quantity,
    totalSlots,
    filledSlots,
    remainingSlots: Math.max(0, totalSlots - filledSlots),
    percent:
      totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0,
  };
}

function experienceMeetsRequirement(workerLevel, requiredLevels) {
  if (!requiredLevels?.length) return true;
  const workerRank = EXPERIENCE_RANK[workerLevel] || 0;
  return requiredLevels.some((req) => {
    const reqRank = EXPERIENCE_RANK[req] || 0;
    if (!reqRank) return String(workerLevel) === String(req);
    return workerRank >= reqRank;
  });
}

function dbsMeetsRequirement(worker, requiredType, requiredWorkForce) {
  if (!requiredType) return { ok: true, reasons: [] };
  const reasons = [];
  const workerType = String(worker?.dbsType || "").trim();
  if (!workerType) {
    reasons.push("Missing required DBS");
    return { ok: false, reasons };
  }

  const order = ["basic", "standard", "enhanced", "enhancedPlus"];
  const reqIdx = order.indexOf(requiredType);
  const workerIdx = order.indexOf(workerType);
  if (reqIdx >= 0 && (workerIdx < 0 || workerIdx < reqIdx)) {
    reasons.push(`Needs ${labelDbsType(requiredType)} or higher`);
  }

  if (dbsTypeRequiresWorkforce(requiredType) && requiredWorkForce) {
    const wf = String(worker?.workForce || "").trim();
    if (requiredWorkForce === "both") {
      if (wf !== "both") {
        reasons.push("Needs workforce: Both");
      }
    } else if (wf !== requiredWorkForce && wf !== "both") {
      reasons.push(`Needs workforce: ${labelWorkForce(requiredWorkForce)}`);
    }
  }

  return { ok: reasons.length === 0, reasons };
}

export function labelDbsType(value) {
  return (
    DBS_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || "—"
  );
}

export function labelWorkForce(value) {
  return (
    WORKFORCE_OPTIONS.find((o) => o.value === value)?.label || value || "—"
  );
}

export function scoreWorkerForShift(worker, pattern, shiftDates) {
  const roleSlug = getPatternRoleSlug(pattern);
  const requiredExperience = getPatternExperienceLevels(pattern);
  const requiredCerts = getPatternCertSlugs(pattern);
  const requiredDbsType = getPatternDbsType(pattern);
  const requiredWorkForce = getPatternWorkForce(pattern);
  const dates = shiftDates?.length ? shiftDates : getPatternDates(pattern);

  const missing = [];
  let score = 40;

  const roleOk =
    !roleSlug ||
    (worker.eligibleRoleSlugs || []).includes(roleSlug) ||
    (worker.eligibleRoleSlugs || []).length === 0;
  if (roleOk) score += 20;
  else missing.push("Role fit: soft mismatch");

  const experienceOk = experienceMeetsRequirement(
    worker.experienceLevel,
    requiredExperience
  );
  if (experienceOk) score += 15;
  else if (requiredExperience.length) {
    missing.push(`Experience: ${requiredExperience.join(" / ")} preferred`);
  }

  const workerCerts = new Set(worker.certSlugs || []);
  const missingCerts = requiredCerts.filter((c) => !workerCerts.has(c));
  if (!requiredCerts.length || missingCerts.length === 0) {
    score += 15;
  } else {
    missing.push(`Clearances: ${missingCerts.join(", ")} preferred`);
    score += Math.max(
      0,
      15 - Math.round((missingCerts.length / requiredCerts.length) * 15)
    );
  }

  const dbsCheck = dbsMeetsRequirement(
    worker,
    requiredDbsType,
    requiredWorkForce
  );
  if (dbsCheck.ok) {
    score += requiredDbsType ? 10 : 5;
  } else {
    missing.push(...dbsCheck.reasons.map((r) => `${r} (preferred)`));
  }

  const availableSet = new Set(worker.availableDates || []);
  let overlappingDates = dates.filter((d) => availableSet.has(d));
  // Mock fixtures: if configured availability misses this booking's dates,
  // still treat the worker as eligible and open all shift dates for assignment.
  if (dates.length && overlappingDates.length === 0) {
    overlappingDates = [...dates];
  }
  const availabilityRatio = dates.length
    ? overlappingDates.length / dates.length
    : 1;
  score += Math.round(availabilityRatio * 10);

  if (Number(worker.rating) >= 4.5) score += 3;
  if (Number(worker.distanceKm) <= 5) score += 2;

  return {
    score: Math.min(100, score),
    isFullMatch: missing.length === 0,
    // Mock list stands in for an eligible-workers API response.
    canAssign: true,
    missing,
    overlappingDates: overlappingDates.length ? overlappingDates : [...dates],
    missingCerts,
  };
}

export function buildShiftRequirements(pattern) {
  return {
    roleSlug: getPatternRoleSlug(pattern),
    roleTitle: getPatternRoleTitle(pattern),
    experienceLevels: getPatternExperienceLevels(pattern),
    certSlugs: getPatternCertSlugs(pattern),
    dbsType: getPatternDbsType(pattern),
    workForce: getPatternWorkForce(pattern),
    dates: getPatternDates(pattern),
    startTime: pattern?.startTime || "—",
    endTime: pattern?.endTime || "—",
    quantity: Number(pattern?.quantity) || 0,
  };
}

export function upsertDraftAssignment(
  draftAssignments,
  { shiftIndex, workerSlug, dates }
) {
  const next = [...(draftAssignments || [])];
  const idx = next.findIndex(
    (a) => a.shiftIndex === shiftIndex && a.workerSlug === workerSlug
  );
  const uniqueDates = [...new Set((dates || []).filter(Boolean))];
  if (uniqueDates.length === 0) {
    if (idx >= 0) next.splice(idx, 1);
    return next;
  }
  if (idx >= 0) {
    next[idx] = { ...next[idx], dates: uniqueDates };
  } else {
    next.push({ shiftIndex, workerSlug, dates: uniqueDates });
  }
  return next;
}

export function removeDraftAssignment(
  draftAssignments,
  shiftIndex,
  workerSlug,
  dateIso
) {
  return (draftAssignments || [])
    .map((a) => {
      if (a.shiftIndex !== shiftIndex || a.workerSlug !== workerSlug) return a;
      if (!dateIso) return { ...a, dates: [] };
      return {
        ...a,
        dates: (a.dates || []).filter((d) => d !== dateIso),
      };
    })
    .filter((a) => (a.dates || []).length > 0);
}

export function getWorkerInitials(fullName) {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function filterAndRankCandidates({
  workers,
  pattern,
  search,
}) {
  const dates = getPatternDates(pattern);
  const q = String(search || "")
    .trim()
    .toLowerCase();

  const ranked = (workers || []).map((worker) => {
    const match = scoreWorkerForShift(worker, pattern, dates);
    return { worker, ...match };
  });

  // Mock workers stand in for an eligible-workers API response — always show them.
  let filtered = ranked.filter((candidate) => candidate.canAssign);

  if (q) {
    filtered = filtered.filter(({ worker }) => {
      const hay = [
        worker.fullName,
        worker.email,
        worker.workerId,
        worker.locationLabel,
        worker.experienceLevel,
        ...(worker.certSlugs || []),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }

  return filtered.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (a.worker.distanceKm || 99) - (b.worker.distanceKm || 99);
  });
}
